import { useSession } from '@/auth/useSession.js';
import { PercentageProgress } from '@/components/percentage-progress.js';
import { SchoolPortalBanner } from '@/components/school-portal-banner.js';
import { getSchoolPortalStudentStats } from '@/lib/api-calls.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { validateRouteRole } from '@/lib/routing.js';
import { isSchoolAdmin, isSchoolAdminRole, optionalPlainDateStringSchema } from '@airreading/common';
import {
  getSchoolPortalStudentsSortOrderSchema,
  studentStatusSchema,
  studentYearSchema,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Atom, User } from 'lucide-react';
import { z } from 'zod';
import { SchoolStudentsTable } from './-components/school-students-table.js';

const schoolStudentsSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  grades: z.array(studentYearSchema).optional(),
  statuses: z.array(studentStatusSchema).optional(),
  classTimeIds: z.array(z.string()).optional(),
  classroomTeachers: z.array(z.string()).optional(),
  attendedStartDate: optionalPlainDateStringSchema,
  attendedEndDate: optionalPlainDateStringSchema,
  sortBy: getSchoolPortalStudentsSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/school-students/')({
  beforeLoad: validateRouteRole(isSchoolAdmin),
  validateSearch: schoolStudentsSearchSchema,
  component: SchoolStudentsPage,
});

function SchoolStudentsPage() {
  const { actingUser } = useSession();
  const { data: stats, error: statsError } = useQuery(getSchoolPortalStudentStats());
  useErrorToast(statsError, 'Failed to load student stats');

  const schoolName = actingUser.userRoles.find((role) => isSchoolAdminRole(role) && role.school?.name)?.school?.name;

  const pageTitle = schoolName ? `${schoolName} - Students` : 'Students';

  return (
    <SchoolPortalBanner
      title={pageTitle}
      data-testid="school-students-header"
      className="flex h-full min-h-0 flex-1 flex-col"
      contentClassName="flex min-h-0 flex-1 flex-col gap-6"
    >
      <div className="flex flex-col gap-4 md:flex-row">
        <div className="flex items-center gap-3 bg-white px-5 py-4 shadow-sm md:min-w-[280px]">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#FDE8EA]">
            <User className="size-5 text-[#EB404A]" aria-hidden />
          </div>
          <div className="flex-1">
            <p className="font-bold text-foreground">Total current students</p>
            <p className="text-sm text-muted-foreground">Total number of currently enrolled students</p>
          </div>
          <p className="text-3xl font-bold text-[#EB404A]" data-testid="total-current-students">
            {stats?.totalCurrentStudents ?? '—'}
          </p>
        </div>

        <div className="flex flex-1 items-start gap-3 bg-white px-5 py-4 shadow-sm">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#FDE8EA]">
            <Atom className="size-5 text-[#EB404A]" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="mb-2 text-sm text-muted-foreground">Average attendance percentage</p>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <PercentageProgress
                label="Past 14 days"
                value={stats?.averageAttendancePast14Days}
                data-testid="attendance-past-14-days"
              />
              <PercentageProgress
                label="Overall"
                value={stats?.averageAttendanceOverall}
                data-testid="attendance-overall"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col bg-white p-4 shadow-sm">
        <SchoolStudentsTable schoolName={schoolName} />
      </div>
    </SchoolPortalBanner>
  );
}
